import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, BookOpen, FileText } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Products", path: "/products" },
  { name: "Industries", path: "/industries" },
  { name: "Contact", path: "/contact" },
];

const resourceLinks = [
  { name: "Blog & Insights", path: "/blog", icon: BookOpen, desc: "Expert guides and industry articles" },
  { name: "Free Guides", path: "/guides", icon: FileText, desc: "Downloadable resources for your facility" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setResourcesOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setResourcesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const isResourceActive = location.pathname.startsWith("/blog") || location.pathname.startsWith("/guides");

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-[#2E2E2E]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img src="/logo-white.png" alt="NG Industries" className="h-10 sm:h-12 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.path
                    ? "text-[#FF5722]"
                    : "text-[#A3A3A3] hover:text-white"
                }`}
              >
                {link.name}
              </Link>
            ))}

            {/* Resources Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                data-testid="nav-resources-dropdown"
                onClick={() => setResourcesOpen(!resourcesOpen)}
                className={`text-sm font-medium transition-colors duration-200 flex items-center gap-1 ${
                  isResourceActive
                    ? "text-[#FF5722]"
                    : "text-[#A3A3A3] hover:text-white"
                }`}
              >
                Resources
                <ChevronDown size={14} className={`transition-transform duration-200 ${resourcesOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {resourcesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full right-0 mt-3 w-64 bg-[#141414] border border-[#2E2E2E] shadow-xl"
                  >
                    {resourceLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        data-testid={`nav-resource-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                        className="flex items-start gap-3 px-4 py-3 hover:bg-[#1F1F1F] transition-colors group"
                      >
                        <link.icon size={16} className="text-[#FF5722] mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-white group-hover:text-[#FF5722] transition-colors">{link.name}</p>
                          <p className="text-xs text-[#666] mt-0.5">{link.desc}</p>
                        </div>
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <Link
              to="/quote"
              data-testid="nav-quote-btn"
              className="hidden sm:inline-flex items-center gap-2 bg-[#FF5722] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#E64A19] transition-colors duration-200"
            >
              <Phone size={14} />
              Request Quote
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-white p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black/95 backdrop-blur-xl border-b border-[#2E2E2E]"
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  className={`block py-3 px-4 text-base font-medium transition-colors ${
                    location.pathname === link.path
                      ? "text-[#FF5722] bg-[#141414]"
                      : "text-[#A3A3A3] hover:text-white hover:bg-[#141414]"
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Mobile Resources */}
              <div className="pt-2 pb-1 px-4">
                <p className="text-xs text-[#666] uppercase tracking-wider font-semibold">Resources</p>
              </div>
              {resourceLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center gap-3 py-3 px-4 text-base font-medium transition-colors ${
                    location.pathname.startsWith(link.path)
                      ? "text-[#FF5722] bg-[#141414]"
                      : "text-[#A3A3A3] hover:text-white hover:bg-[#141414]"
                  }`}
                >
                  <link.icon size={16} />
                  {link.name}
                </Link>
              ))}

              <Link
                to="/quote"
                data-testid="mobile-quote-btn"
                className="block py-3 px-4 text-base font-semibold text-white bg-[#FF5722] text-center mt-2"
              >
                Request Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
